/**
 * Commentary section in a rich chat response.
 * Parses commentary text for inline Strong's chips
 * and preserves [Halley]/[McGee]/[Smith] attributions.
 *
 * V6.4: pericope commentary arrives as verse-anchored blocks
 * (`Verses 14-16\n<prose>` separated by blank lines). When any block
 * opens with an anchor, each block renders with a tappable anchor chip
 * above its prose. Bodies without anchors (verse mode, degraded pericope
 * rolls, pre-V6.4 history) render exactly as before — one segment pass.
 *
 * Header actions removed — copy / report now live in the single
 * Report button at the bottom of the response (see ReportButton).
 */

import { splitAnchorBlock } from '@skjv/common';
import { parseMessageSegments, type MessageSegment } from './parse-message-segments';
import { InlineStrongsChip } from './inline-strongs-chip';
import { InlineVerseRef } from './inline-verse-ref';
import { CommentaryAnchorChip, type AnchorContext } from './commentary-anchor-chip';

interface CommentarySectionProps {
  commentary: string;
  /** Passage the anchors resolve against (pericope book+chapter). Absent →
   *  anchor chips render as static labels. */
  anchorContext?: AnchorContext | null;
}

/** Resolve the anchor context from whatever the call site has: pericope
 *  info when present, else the anchor reference string ("Revelation 3:14").
 *  Pass `verses` where the passage text is at hand (live pericope study) —
 *  it upgrades the anchor labels from "Verses 4-6" pills to the actual
 *  scripture text. */
export function deriveAnchorContext(
  pericope: { book: string; chapter: number } | null | undefined,
  reference: string | undefined,
  verses?: Array<{ verse: number; text: string }>,
): AnchorContext | null {
  if (pericope) return { book: pericope.book, chapter: pericope.chapter, verses };
  const m = reference?.match(/^(.+?)\s+(\d+):\d+/);
  return m ? { book: m[1], chapter: +m[2], verses } : null;
}

/** KJV text of an anchor's verse range, or null when unavailable. */
function scriptureForRange(
  context: AnchorContext | null | undefined,
  start: number,
  end: number,
): string | null {
  if (!context?.verses?.length) return null;
  const inRange = context.verses
    .filter((v) => v.verse >= start && v.verse <= end)
    .sort((a, b) => a.verse - b.verse);
  if (inRange.length === 0) return null;
  return inRange.map((v) => v.text.trim()).join(' ');
}

/**
 * Drop the response's in-body title block. The API opens pericope commentary
 * with `BIBLE PERICOPE COMMENTARY — <ref>` + the pericope name; the In Context
 * card directly above already shows both, so printing it here said everything
 * twice (UAT, 2026-09-21). The stored text keeps it — reports and history are
 * untouched; this is a render-time scrub, the same rule the PDF applies
 * (pdf-commentary-page.ts).
 */
function withoutTitleBlock(blocks: string[]): string[] {
  return blocks.filter((b, i) => !(i === 0 && b.trim().startsWith('BIBLE PERICOPE COMMENTARY')));
}

function renderSegment(segment: MessageSegment, index: number) {
  switch (segment.type) {
    case 'strongs':
      return (
        <InlineStrongsChip
          key={`cs-${index}`}
          number={segment.number}
          displayText={segment.displayText}
        />
      );
    case 'verse-ref':
      return (
        <InlineVerseRef
          key={`cv-${index}`}
          reference={segment.reference}
          displayText={segment.displayText}
        />
      );
    case 'text':
    default:
      return <span key={`ct-${index}`}>{segment.content}</span>;
  }
}

export function CommentarySection({ commentary, anchorContext }: CommentarySectionProps) {
  if (!commentary) return null;

  const blocks = withoutTitleBlock(commentary.split(/\n{2,}/));
  if (blocks.length === 0) return null;
  const hasAnchors = blocks.some((b) => splitAnchorBlock(b).anchor !== null);

  // Un-anchored shape (verse mode / degraded pericope / recalled pre-V6.4
  // commentary) — the historical single-pass render, pixel-identical.
  if (!hasAnchors) {
    const segments = parseMessageSegments(blocks.join('\n\n'));
    return (
      <div className="rich-message-section commentary-section">
        <h4 className="rich-message-section-title">Commentary</h4>
        <div className="commentary-text">
          {segments.map((seg, i) => renderSegment(seg, i))}
        </div>
      </div>
    );
  }

  return (
    <div className="rich-message-section commentary-section">
      <h4 className="rich-message-section-title">Commentary</h4>
      <div className="commentary-text">
        {blocks.map((block, bi) => {
          const { anchor, anchorText, prose } = splitAnchorBlock(block);
          return (
            <div key={`cb-${bi}`} className="commentary-block">
              {anchor && (
                <CommentaryAnchorChip
                  displayText={anchorText as string}
                  startVerse={anchor.start}
                  context={anchorContext ?? null}
                  scripture={scriptureForRange(anchorContext, anchor.start, anchor.end)}
                />
              )}
              <div className="commentary-block-prose">
                {parseMessageSegments(anchor ? prose : block).map((seg, i) =>
                  renderSegment(seg, i),
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
